<template>
  <div class="plan-container plan-container__assign-audit">
    <p class="title">Audit Assignment</p>
    <div class="sub-field">
      <p class="subtitle">Click on a user to start assigning audits.</p>
      <div class="search-params">
        <div class="dateFilter">
          From:
          <input type="date" name="search" v-model="dateRange.start" />
          To:
          <input type="date" name="search" v-model="dateRange.end" />
        </div>
        <div class="search-bar field no-margin">
          <span class="icon input-icon">
            <i class="fas fa-search"></i>
          </span>
          <input
            type="text"
            class="input has-icon"
            placeholder="Search by name or email"
            v-model="searchTerm"
            @input="search"
          />
        </div>
      </div>
    </div>
    <div class="plan-list">
      <div class="table-wrapper">
        <table class="table" id="assignments-table">
          <thead>
            <tr>
              <th class="sortable-header" id="name" @click="didClickSort">
                Name
                <span class="ordered-icon"></span>
              </th>
              <th class="sortable-header" id="email" @click="didClickSort">
                Email
                <span class="ordered-icon"></span>
              </th>
              <th class="sortable-header" id="role" @click="didClickSort">
                Role / Position
                <span class="ordered-icon"></span>
              </th>
              <th id="assigned">
                # Assigned
                <span class="ordered-icon"></span>
              </th>
              <th id="incomplete">
                # Not Completed
                <span class="ordered-icon"></span>
              </th>
              <th id="completed">
                # Completed
                <span class="ordered-icon"></span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in filteredUsers"
              :key="user.username"
              @click="selectedUser = user"
              :class="{ selected: selectedUser._id === user._id }"
            >
              <td>{{ user.name }}</td>
              <td>{{ user.email }}</td>
              <td>{{ user.role }}</td>
              <td>{{ totalAssignments(user._id) }}</td>
              <td>{{ outstandingAssignments(user._id) }}</td>
              <td>{{ completedAssignments(user._id) }}</td>
            </tr>

            <tr>
              <td></td>
              <td></td>
              <td>
                <strong>Totals:</strong>
              </td>
              <td>
                {{ assignmentsDateFiltered.length }}
              </td>
              <td>
                {{ assignmentsDateFiltered.filter(a => !a.completedAt).length }}
              </td>
              <td>
                {{ assignmentsDateFiltered.filter(a => a.completedAt).length }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="info-row info-row--flush">
        <button
          class="button"
          @click="showAddUser = true"
          :disabled="!selectedUser._id"
        >
          Assign Audit
        </button>
      </div>
    </div>
    <div class="modal is-active" v-if="false">
      <div class="modal-background" @click.stop="showSplitAudit = false"></div>
      <div class="modal-content modal-content--wide">
        <div class="title">Assign several staff to a single audit</div>
        <hr />
        <div class="audit-params">
          <div class="select-plan">
            <AuditSelection
              :audits="auditsList"
              :selected="selectedSplitAudit.audit"
              @audit-selected="assignSplitAudit"
            />
          </div>
          <div class="select-auditors">
            <ul>
              <li
                v-for="user in users"
                :key="user._id"
                @click="assignSplitAuditors(user._id)"
                :class="{
                  selected: selectedSplitAudit.auditors.includes(user._id)
                }"
              >
                {{ user.name }}
              </li>
            </ul>
          </div>
        </div>
        <div class="field">
          <button
            class="button"
            @click="didClickAssignSplitAudit"
            :disabled="assignSplitDisabled"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
    <div class="modal is-active" v-if="showAddUser">
      <div class="modal-background" @click.stop="showAddUser = false"></div>
      <div class="modal-content">
        <div class="title">Assign Audits</div>
        <hr />
        <div class="field">
          <label for="name">Name</label>
          <span class="icon input-icon">
            <i class="far fa-user"></i>
          </span>
          <input
            type="text"
            class="input has-icon"
            id="name"
            disabled
            v-model="selectedUser.name"
          />
        </div>
        <div class="field">
          <label for="name">Email</label>
          <span class="icon input-icon">
            <i class="far fa-envelope"></i>
          </span>
          <input
            type="text"
            class="input has-icon"
            id="email"
            disabled
            v-model="selectedUser.email"
          />
        </div>
        <div class="unnassigned-wrapper">
          <div class="search-wrapper">
            <div class="field search-field">
              <span class="icon input-icon">
                <i class="fa fa-search"></i>
              </span>
              <input
                type="text"
                class="input has-icon"
                placeholder="Search by ID"
                id="email"
                v-model="searchPlanTerm"
              />
            </div>
          </div>
          <div class="unassigned">
            <AuditSelection
              :audits="auditsList"
              :selected="selectedAudits"
              :auditDeadlines="auditDeadlines"
              @audit-selected="assignAudit"
              @audit-deadline-changed="changeDeadline"
              @audit-unselected="unselectAudit"
              :bus="bus"
            />
          </div>
        </div>
        <div class="field">
          <button class="button" @click="submitAssignedPlans">Assign</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { parseISO, differenceInCalendarMonths, format } from 'date-fns'
import Fuse from 'fuse.js'
import AuditSelection from '@/components/Administrator/Lists/AuditSelection.vue'
import { displayWarning } from '@/utils/notification-factory'
import { message } from '@/utils/app-messages'

export default {
  name: 'AuditAssign',
  components: { AuditSelection },
  data() {
    return {
      showAddUser: false,
      showSplitAudit: false,
      selectedUser: {
        _id: null
      },
      toAssign: {},
      selectedAudits: [],
      auditDeadlines: {},
      selectedSplitAudit: {
        audit: null,
        auditors: []
      },
      canAssignSplitAudit: false,
      searchTerm: '',
      searchPlanTerm: '',
      fuse: null,
      searchResults: [],
      dateRange: {
        start: null,
        end: null
      },
      lastSortedBy: null,
      bus: new Vue()
    }
  },
  computed: {
    ...mapGetters({
      plans: 'plan/plans',
      assignablePlans: 'plan/assignablePlans',
      users: 'organisation/users',
      assignments: 'assignment/assignments',
      audits: 'assignment/audits'
    }),
    auditsList() {
      let plans = [...this.assignablePlans]
        // Sort by created date.
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

      // Filter by search if we're searching.
      if (this.searchPlanTerm && this.searchPlanTerm.length > 0) {
        plans = plans.filter(({ internalId }) =>
          internalId.toLowerCase().includes(this.searchPlanTerm.toLowerCase())
        )
      }

      // Return the plans.
      return plans
    },
    unassigned() {
      const assigned = Object.entries(this.toAssign).flat(2)
      const toReturn = this.plans
        .slice()
        .filter(p => assigned.indexOf(p._id) === -1)
      return toReturn
    },
    assignedAudits() {
      return this.audits.filter(audit => !audit.assignmentId.archived)
    },
    assignmentsDateFiltered() {
      const fas = this.assignments
        .filter(
          a =>
            // Pass through if we don't have date start or completed at.
            !this.dateRange.start ||
            !a.createdAt ||
            // Or filter by them.
            new Date(a.createdAt) >= this.fromDate
        )
        .filter(
          a =>
            // Pass through if we don't have date end or completed at.
            !this.dateRange.end ||
            !a.createdAt ||
            // Or filter by them.
            new Date(a.createdAt) <= this.toDate
        )

      console.log(fas)

      return fas
    },
    assignedAuditsByUser() {
      return this.assignedAudits.map(audit => {
        return {
          auditId: audit._id,
          userId: audit.auditorId,
          status:
            audit.completedAt && audit.assignmentId.completedAt
              ? 'completed'
              : audit.assignmentId.startedAt && !audit.completedAt
                ? 'started'
                : 'assigned',
          completedAt: audit.completedAt || audit.assignmentId.completedAt,
          auCa: audit.completedAt || null,
          assCa: audit.assignmentId.completedAt || null,
          assCreated: audit.assignmentId.createdAt || null,
          auSa: audit.assignmentId.startedAt || null,
          numSections: audit.sections.length
        }
      })
    },
    completedAuditsByUser() {
      return this.assignedAuditsByUser.filter(
        audit => audit.status === 'completed'
      )
    },
    filteredUsers() {
      return this.searchResults.length
        ? [...this.users].filter(user => this.searchResults.includes(user._id))
        : this.users
    },
    fromDate() {
      return new Date(this.dateRange.start)
    },
    toDate() {
      const date = new Date(this.dateRange.end)
      // Set time to EOD.
      date.setHours('23')
      date.setMinutes('59')
      date.setSeconds('59')
      return date
    },
    assignSplitDisabled() {
      return !(
        this.selectedSplitAudit.audit &&
        this.selectedSplitAudit.auditors.length > 0
      )
    }
  },
  filters: {
    formatDate(val) {
      return format(new Date(val), 'PPP')
    }
  },
  methods: {
    ...mapActions({
      fetchAssignablePlans: 'plan/fetchAssignablePlans',
      createAssignment: 'assignment/createAssignment',
      fetchAllAssignments: 'assignment/fetchAllAssignments'
    }),
    changeDeadline({ plan, deadline }) {
      this.auditDeadlines = {
        ...this.auditDeadlines,
        [plan]: deadline
      }
    },
    assignAudit(planId) {
      if (this.selectedAudits.find(a => a.planId === planId)) {
        this.selectedAudits = this.selectedAudits.filter(
          a => a.planId !== planId
        )
        return
      }
      const matchingAssignmentIdx = this.assignments.findIndex(
        assignment =>
          (assignment.planId && assignment.planId._id === planId) &&
          (assignment.userId && assignment.userId._id === this.selectedUser._id)
      )

      if (matchingAssignmentIdx === -1) {
        this.selectedAudits.push({ planId, userId: this.selectedUser._id })
      } else {
        displayWarning(message.PLAN_ALREADY_ASSIGNED(this.selectedUser.name))
        this.bus.$emit('reset-active-item-status', planId)
      }
    },
    assignSplitAudit(planId) {
      console.log({ planId })
      this.selectedSplitAudit.audit = planId
    },
    assignSplitAuditors(userId) {
      console.log({ userId })
      if (
        this.selectedSplitAudit.auditors.find(auditorId => auditorId === userId)
      ) {
        this.selectedSplitAudit.auditors = this.selectedSplitAudit.auditors.filter(
          auditorId => auditorId !== userId
        )
        return
      }
      if (this.selectedSplitAudit.auditors.length === 3) {
        this.selectedSplitAudit.auditors.pop()
      }
      this.selectedSplitAudit.auditors.push(userId)
    },
    hasAssignment(plan) {
      console.log({ plan })
      return Object.prototype.hasOwnProperty.call(
        this.toAssign,
        this.selectedUser._id
      )
        ? this.toAssign[this.selectedUser.username].find(p => p === plan._id)
        : false
    },
    totalAssignments(userId) {
      return this.assignmentsDateFiltered.filter(ass =>
        ass.userId._id === userId ? ass : null
      ).length
    },
    outstandingAssignments(userId) {
      // Filter audits...
      return this.assignmentsDateFiltered.filter(
        ass =>
          // By userId.
          ass.userId._id === userId &&
          // where not completed.
          !ass.completedAt
      ).length
    },
    completedAssignments(userId) {
      // Filter audits...
      return (
        this.assignedAuditsByUser
          .filter(
            audit =>
              // Pass through if we don't have date start or completed at.
              !this.dateRange.start ||
              !audit.assCreated ||
              // Or filter by them.
              new Date(audit.assCreated) >= this.fromDate
          )
          .filter(
            audit =>
              // Pass through if we don't have date end or completed at.
              !this.dateRange.end ||
              !audit.assCreated ||
              // Or filter by them.
              new Date(audit.assCreated) <= this.toDate
          )
          // By userId.
          .filter(audit => audit.userId === userId)
          // By complete status
          .filter(audit => audit.status === 'completed').length
      )
    },
    completedAudits(userId) {
      return this.assignedAuditsByUser
        .slice()
        .filter(audit => audit.userId === userId)
        .filter(audit => audit.startedAt && audit.completedAt).length
    },
    async submitAssignedPlans() {
      if (!Object.keys(this.selectedAudits).length) {
        return displayWarning(message.NO_PLANS_ASSIGNED)
      }

      this.saving = true
      try {
        const assignable = []

        this.selectedAudits.forEach(({ planId, userId }) =>
          assignable.push(
            this.createAssignment({
              planId,
              userId,
              deadline: this.auditDeadlines[planId]
                ? this.auditDeadlines[planId]
                : undefined
            })
          )
        )
        await this.fetchAllAssignments()
        this.showAddUser = false
        this.selectedUser = { _id: null }
        this.selectedAudits = []
      } catch (error) {
        displayWarning(error)
      }

      // Refresh list.
      this.fetchAssignablePlans()
      this.saving = false
    },
    monthsSinceToday(date) {
      const beginningDate = date instanceof Date ? date : parseISO(date)
      return differenceInCalendarMonths(beginningDate, new Date())
    },
    search(event) {
      if (!this.fuse) {
        this.fuse = new Fuse(this.users, {
          includeScore: true,
          ignoreLocation: true,
          threshold: 0.25,
          keys: ['email', 'name']
        })
      }
      const results = this.fuse.search(this.searchTerm)
      this.searchResults = results.map(r => r.item._id)
    },
    didClickSort(event) {
      const shouldSortDescending = this.lastSortedBy === event.target.id
      const clicked = event.target.id
      console.log({ clicked })
      console.log(this.filteredUsers.map(a => a[clicked].toLowerCase()))
      const comparator = shouldSortDescending
        ? (a, b) =>
          a[clicked].toLowerCase() < b[clicked].toLowerCase() ? -1 : 1
        : (a, b) =>
          a[clicked].toLowerCase() > b[clicked].toLowerCase() ? -1 : 1
      const sorted = this.filteredUsers.sort(comparator)
      console.log({ sorted })
      this.lastSortedBy = event.target.id
    },
    didClickAssignSplitAudit() {
      displayWarning(message.BROWSER_UPDATE_REQUIRED)
    },
    // Removes audit when unselected.
    unselectAudit(planId) {
      delete this.auditDeadlines[planId]
      this.selectedAudits = this.selectedAudits.filter(
        ({ planId: _id }) => _id !== planId
      )
    }
  },
  mounted() {
    this.fetchAllAssignments()
    this.fetchAssignablePlans()
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';
@import '@/style/modal.scss';

.field {
  display: flex;
  flex-flow: column;
  margin-bottom: 1rem;
  label {
    font-weight: 600;
    font-size: 0.9rem;
    padding-bottom: 0.5rem;
  }

  button {
    margin-top: 16px;
  }
}

span.icon {
  bottom: 0rem;
}

.search-wrapper {
  border-bottom: 1px lightgrey solid;
  width: 100%;
}

.search-field input {
  border: none;
  outline: none;
  width: 99%;
}

.search-field {
  margin-bottom: 0px;
  margin-left: 5px;
  width: 90%;
  outline: none;
  border: none;
}
.unnassigned-wrapper {
  border: 1px solid lightgrey;
  border-radius: $border-radius;
}

.unassigned {
  width: 100%;
  max-height: 300px;
  min-height: 320px;

  overflow-y: scroll;
}

.unassigned > ul {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.unassigned > ul > li {
  padding: 0.75rem;
}
.unassigned > ul > li:hover,
.to-assign {
  background: $brand-gradient;
  color: white;
  cursor: pointer;
}

.modal-content {
  width: 100%;
  min-width: 400px;
  max-width: 550px;
  &--wide {
    max-width: 1200px;
    max-height: 500px;
    hr {
      margin: 0.5rem 0;
    }
  }
}

.modal-content > .title {
  text-align: center;
  padding: 0;
  margin: 10px 0;
}

hr {
  height: 1px;
  width: 100%;
  border-color: $fill-grey;
  margin-bottom: 20px;
}

.no-margin {
  margin: 0;
}

.sub-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-area: search;
  .subtitle {
    width: 100%;
  }
}

.search-params {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  height: 100%;
  width: 100%;
}

.dateFilter {
  flex-grow: 1;
  flex-shrink: 0;
  padding-right: 0.5rem;
}

.search-bar {
  max-width: 400px;
}

.sortable-header {
  cursor: pointer;
}

span.ordered-icon {
  display: none;
  visibility: hidden;
}

span.ordered-icon.active {
  display: inline-block;
  visibility: visible;
  position: relative;
  .asc {
    transform: rotate(0deg);
  }
  .desc {
    transform: rotate(180deg);
  }
}

span.ordered-icon:after {
  position: absolute;
  content: '^';
}

.audit-params {
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: 3fr 2fr;
  column-gap: 0.5rem;
  height: 100%;
  overflow-y: overlay;
  .select-plan {
    height: 100%;
    overflow-y: overlay;
  }
  .select-auditors {
    ul {
      margin: unset;
      padding: 0.5rem;
      list-style-type: none;
    }
    li {
      cursor: pointer;
      padding: 0.5rem;
      &:hover {
        background-color: #eeedec;
        text-decoration: underline;
      }
    }
    .selected {
      font-weight: 600;
      text-decoration: underline;
    }
  }
}
button.button:disabled {
  background: lighten($color: $brand-green, $amount: 25%);
  cursor: not-allowed;
}
</style>
